import {
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  ApplicationNote,
  NoteAttachment,
  CreateNotePayload,
  EditNotePayload
} from "shared/app/Notes/types";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { useCallback, useMemo, useState } from "react";

type NotesListResponse = {
  object: "list";
  items: ApplicationNote[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
  extra?: {
    mentioned_notes_count?: number;
    total_notes_count?: number;
  };
};

type AttachmentsListResponse = {
  object: "list";
  items: NoteAttachment[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
};

const notesQueryKey = (
  applicationId: number | string,
  queryParams?: Record<string, string | string[] | boolean>
) => ["NOTES", applicationId, queryParams];

export enum ShowingNotesType {
  ALL = "ALL",
  MENTIONS = "MENTIONS",
  ATTACHMENTS = "ATTACHMENTS"
}

export const useNotesController = (
  applicationId: string,
  onNoteChange?: () => void
) => {
  const queryClient = useQueryClient();
  const [showingNotesType, setShowingNotesType] = useState<ShowingNotesType>(
    ShowingNotesType.ALL
  );

  const mentionedOnly = showingNotesType === ShowingNotesType.MENTIONS;
  const isAttachmentsTab = showingNotesType === ShowingNotesType.ATTACHMENTS;

  const notesMeta = useInfiniteQuery<NotesListResponse>({
    queryKey: notesQueryKey(applicationId, { mentioned_only: mentionedOnly }),
    queryFn: ({ pageParam }) =>
      instance
        .get(`portal/v1/applications/${applicationId}/notes`, {
          params: {
            per_page: 20,
            page: pageParam,
            mentioned_only: mentionedOnly
          }
        })
        .then(({ data }) => data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled: Boolean(applicationId && applicationId !== "undefined")
  });

  const attachmentsMeta = useInfiniteQuery<AttachmentsListResponse>({
    queryKey: ["NOTES", "ATTACHMENTS", applicationId],
    queryFn: ({ pageParam }) =>
      instance
        .get(`portal/v1/applications/${applicationId}/notes/attachments`, {
          params: { per_page: 20, page: pageParam }
        })
        .then(({ data }) => data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled:
      Boolean(applicationId && applicationId !== "undefined") &&
      isAttachmentsTab
  });

  const onNotesMutationSuccess = useCallback(() => {
    notesMeta.refetch();
    queryClient.invalidateQueries({
      queryKey: ["NOTES", "ATTACHMENTS", applicationId]
    });
    onNoteChange?.();
  }, [notesMeta, queryClient, applicationId]);

  const onAttachmentMutationSuccess = useCallback(() => {
    attachmentsMeta.refetch();
    queryClient.invalidateQueries({
      queryKey: notesQueryKey(applicationId, { mentioned_only: true })
    });
    queryClient.invalidateQueries({
      queryKey: notesQueryKey(applicationId, { mentioned_only: false })
    });
    onNoteChange?.();
  }, [attachmentsMeta, queryClient, applicationId]);

  const addNoteMeta = useMutation({
    mutationFn: (newNote: CreateNotePayload) =>
      addressApi.addApplicationNote(Number(applicationId), newNote),
    mutationKey: [...notesQueryKey(applicationId), "CREATE"],
    onSuccess: onNotesMutationSuccess,
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const editNoteMeta = useMutation({
    mutationFn: ({ noteId, data }: { noteId: number; data: EditNotePayload }) =>
      addressApi.editApplicationNote(Number(applicationId), noteId, data),
    mutationKey: [...notesQueryKey(applicationId), "UPDATE"],
    onSuccess: onNotesMutationSuccess,
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const deleteNoteMeta = useMutation({
    mutationFn: (noteId: number) =>
      addressApi.deleteApplicationNote(Number(applicationId), noteId),
    mutationKey: [...notesQueryKey(applicationId), "DELETE"],
    onSuccess: onNotesMutationSuccess,
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const deleteAttachmentMeta = useMutation({
    mutationFn: (attachmentId: number) =>
      addressApi.deleteNoteAttachment(Number(applicationId), attachmentId),
    mutationKey: [...notesQueryKey(applicationId), "ATTACHMENT", "DELETE"],
    onSuccess: onAttachmentMutationSuccess,
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const changeShowingNotesType = (type: ShowingNotesType) => {
    setShowingNotesType(type);
  };

  const allNotes = useMemo(() => {
    return notesMeta.data?.pages.flatMap((page) => page.items) ?? [];
  }, [notesMeta.data]);

  const mentionNotesCount = useMemo(() => {
    return notesMeta.data?.pages[0]?.extra?.mentioned_notes_count ?? 0;
  }, [notesMeta.data]);

  const totalNotesCount = useMemo(() => {
    return notesMeta.data?.pages[0]?.extra?.total_notes_count ?? 0;
  }, [notesMeta.data]);

  const allAttachments = useMemo(() => {
    return attachmentsMeta.data?.pages.flatMap((page) => page.items) ?? [];
  }, [attachmentsMeta.data]);

  const attachmentsCount = useMemo(() => {
    return attachmentsMeta.data?.pages[0]?.total_count ?? 0;
  }, [attachmentsMeta.data]);

  return {
    notesMeta,
    showingNotes: allNotes,
    deleteNoteMeta,
    editNoteMeta,
    addNoteMeta,
    mentionNotesCount,
    totalNotesCount,
    showingNotesType,
    changeShowingNotesType,
    attachmentsMeta,
    deleteAttachmentMeta,
    allAttachments,
    attachmentsCount,
    isAttachmentsTab
  };
};
